import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import pageSizes from '../../../../components/PageSizeButton/pageSizes';
import usePagination from '../../../../hooks/usePagination';
import useDebouncedSearch from '../../../../hooks/useDebouncedSearch';
import PageSizeButton from '../../../../components/PageSizeButton/PageSizeButton';
import SearchField from '../../../../components/SearchField/SearchField';
import RequestsTable from '../RequestsTable/RequestsTable';
import ActionsDropdown from '../ActionsDropdown/ActionsDropdown';
import { getReplacedString } from '../../../../helpers/getReplacedString';
import createExcelFromRequestsArray from '../../../../helpers/createExcelFromRequestsArray';
import { requestActionNames, sortParams } from '../../constants/common';
import getRequestsFileToPrint from '../../../../requests/getRequestsFileToPrint';
import DspxPagination from '../../../../components/DspxPagination/DspxPagination';
import { dspxSorter } from '../../../../helpers/dspx-sort';
import styles from './RequestsContainer.module.scss';
import { ToggleShowRequests } from '../ToggleShowRequests/ToggleShowRequests';

const orders = {
  ascend: 'Asc',
  descend: 'Desc',
};

const getSortParam = ({ columnKey }) => {
  switch (columnKey) {
    case 'num/create':
      return sortParams.ID;

    case 'status':
      return sortParams.STATUS;

    case 'master/executer':
      return sortParams.MASTER;

    case 'parentService/service':
      return sortParams.SERVICE;

    case 'done':
      return sortParams.EXECUTE_TIME;

    case 'address':
      return sortParams.ADDRESS;

    default:
      throw new Error(`Не нашлось соответствия с columnKey = ${columnKey}`);
  }
};

const RequestsContainer = ({
  rootClassName,
  customPageSize = false,
  actionsDropdownClassName,
  setShowRequests,
  showRequests = true,
  tableClassname,
  showInLifeCirle,
}) => {
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [filteredBySearchRequests, setFilteredBySearchRequests] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: 'num/create',
    param: sortParams.ID,
    order: 'descend',
  });
  const location = useLocation();

  const filteredRequests = useSelector(
    (state) => state.statisticsStore.filteredRequests
  );

  const [search, setSearch, debouncedSearch] = useDebouncedSearch();

  const [
    currentPageNumber,
    setCurrentPageNumber,
    maxPage,
    shownRequests,
    firstIndexOnPage,
  ] = usePagination(filteredBySearchRequests, pageSize);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setSelectedRequests([]);
  };

  const handleSelectAllRequests = (e) => {
    let requests = [];

    if (e.target.checked) {
      requests = filteredBySearchRequests.map((request) => request.id);
    }

    setSelectedRequests(requests);
  };

  const handleSelectRequest = (requestId) => {
    const selectedIndex = selectedRequests.indexOf(requestId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRequests, requestId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRequests.slice(1));
    } else if (selectedIndex === selectedRequests.length - 1) {
      newSelected = newSelected.concat(selectedRequests.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRequests.slice(0, selectedIndex),
        selectedRequests.slice(selectedIndex + 1)
      );
    }

    setSelectedRequests(newSelected);
  };

  const handleActionsMenuClick = (key) => {
    if (key === requestActionNames.excel) {
      createExcelFromRequestsArray(selectedRequests, filteredBySearchRequests);
    }

    if (key === requestActionNames.print) {
      getRequestsFileToPrint(selectedRequests);
    }
  };

  const handlePageChange = (page) => {
    const pageNum = page - 1;

    setCurrentPageNumber(pageNum);
  };

  const handleExtraSort = ({ key, param }) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) {
        return { key, param, order: 'ascend' };
      }

      if (param === prev.param) {
        return null;
      }

      return {
        ...prev,
        order: prev.order || 'ascend',
        param,
      };
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) {
        return {
          key,
          param: getSortParam({ columnKey: key }),
          order: 'ascend',
        };
      }

      if (prev.order === 'descend') {
        return {
          ...prev,
          order: undefined,
        };
      }

      return {
        ...prev,
        order: !prev.order ? 'ascend' : 'descend',
      };
    });
  };

  useEffect(() => {
    const val = debouncedSearch ?? '';

    const newRequests = val.trim()
      ? filteredRequests.filter((request) => {
          const searchValue = getReplacedString(val);
          return String(request.id).includes(searchValue);
        })
      : filteredRequests.slice();

    if (sortConfig) {
      const { param, order } = sortConfig;
      const sorterKey = `sorter${param}${orders[order]}`;
      const sorter = dspxSorter[sorterKey];
      newRequests.sort(sorter);
    }

    setFilteredBySearchRequests(newRequests);
  }, [filteredRequests, debouncedSearch, sortConfig]);

  useEffect(() => {
    setCurrentPageNumber(0);
    setSearch('');
    setSelectedRequests([]);
  }, [location.search, setCurrentPageNumber, setSearch]);

  const handleShowRequests = () => {
    if (typeof setShowRequests !== 'function') return;
    setShowRequests((prev) => !prev);
  };

  return (
    <div className={cn(styles.root, rootClassName)}>
      <div
        className={cn(styles.wrapper, customPageSize && styles.wrapper_custom)}
      >
        {!customPageSize && (
          <PageSizeButton
            pageSize={pageSize}
            setPageSize={setPageSize}
            buttonClassName={styles.pageSizeBtn}
            className={styles.pageSizeBtnWrap}
            invert
          />
        )}
        <SearchField
          className={cn(styles.searchInput, styles.searchInput_custom)}
          placeholder="Поиск по номеру заявки"
          value={search}
          onChange={handleSearchChange}
        />
        <ActionsDropdown
          className={actionsDropdownClassName}
          disabled={selectedRequests.length === 0}
          onMenuClick={handleActionsMenuClick}
        />
      </div>
      <RequestsTable
        tableClassname={tableClassname}
        requests={showRequests ? shownRequests : []}
        onSelectAll={handleSelectAllRequests}
        onSelect={handleSelectRequest}
        selectedRequests={selectedRequests}
        rowCount={filteredBySearchRequests.length}
        onSort={handleSort}
        onExtraSort={handleExtraSort}
        sortConfig={sortConfig}
        showInLifeCirle={showInLifeCirle}
      />
      {typeof setShowRequests === 'function' && (
        <div className={styles.autoUpdateBlock}>
          <ToggleShowRequests
            enabled={showRequests}
            onChange={handleShowRequests}
          />
          {showRequests && (
            <PageSizeButton
              customText="строк на странице"
              showText={false}
              pageSize={pageSize}
              setPageSize={setPageSize}
              buttonClassName={styles.pageSizeBtn}
              className={styles.pageSizeBtnWrap}
              invert
            />
          )}
        </div>
      )}
      {showRequests && (
        <div className={styles.paginationBlock}>
          {filteredBySearchRequests.length > pageSize && (
            <div className={styles.paginationInfo}>
              <p className={styles.text}>
                С {firstIndexOnPage + 1} по{' '}
                {firstIndexOnPage + shownRequests.length} из{' '}
                {filteredBySearchRequests.length}
              </p>
            </div>
          )}
          <DspxPagination
            current={currentPageNumber + 1}
            total={filteredBySearchRequests.length}
            pageSize={pageSize}
            showQuickJumper={maxPage > 9}
            quickJumperToStart
            onChange={handlePageChange}
            hideOnSinglePage
          />
        </div>
      )}
    </div>
  );
};

export default RequestsContainer;
